import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { useIwEditingStore, WitnessesItem } from '@/entities/interwills';
import { getBeneficiaryFullName } from '@/entities/interwills/helpers';
import { useUploadVerificationRecordings } from '@/entities/interwills/hooks/useUploadVerificationRecordings';
import { useIwRecordingStore } from '@/entities/interwills/stores/iwRecording.store';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { RecordingConfirmModalProps } from '@/widgets/modals/types';

import { ModalContainerV2 } from '../containers/ModalContainerV2';

export const RecordingConfirmModal: FC<RecordingConfirmModalProps> = () => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'recordingConfirm' });

  const editing = useIwEditingStore();
  const recording = useIwRecordingStore();
  const modals = useModalsStore();
  const { uploadRecordings } = useUploadVerificationRecordings();

  const [loading, setLoading] = useState(false);
  const witnessMap = useMemo(() => {
    return editing.witnesses.reduce<Record<DtoId, WitnessesItem>>((acc, curr) => {
      if (!curr.id) return acc;
      acc[curr.id] = curr;
      return acc;
    }, {});
  }, [editing, recording]);

  const onSubmit = async () => {
    setLoading(true);
    await uploadRecordings();
    setLoading(false);

    modals.closeModal();
  };

  return (
    <ModalContainerV2 pinBottom title={t('title')}>
      <Content>
        <span>{t('approve')}</span>
        <List>
          {recording.testator && (
            <li>
              {t('verificationBy')} {t('testator')}{' '}
              {getBeneficiaryFullName({
                firstName: editing.testator.firstName ?? '',
                lastName: editing.testator.lastName ?? '',
              })}
            </li>
          )}
          {Object.keys(recording.witnesses).map((key) => {
            if (!recording.witnesses[key]) return null;

            return (
              <li key={key}>
                {t('verificationBy')} {t('witness')}{' '}
                {getBeneficiaryFullName({
                  firstName: witnessMap[key]?.firstName ?? '',
                  lastName: witnessMap[key]?.lastName ?? '',
                })}
              </li>
            );
          })}
        </List>
        <span>{t('videoNumber')}</span>
        <Buttons>
          <ButtonIw
            onClick={() => {
              modals.closeModal();
            }}
            type="default"
          >
            {t('cancel')}
          </ButtonIw>
          <ButtonIw disabled={loading} loading={loading} onClick={onSubmit}>
            {t('confirm')}
          </ButtonIw>
        </Buttons>
      </Content>
    </ModalContainerV2>
  );
};

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: 16px;
  gap: 16px;
`;

const Content = styled.div`
  padding: 24px 0 0;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const List = styled.ul`
  padding-left: 16px;
  display: flex;
  gap: 8px;
  flex-direction: column;
`;
